import { MouseEvent, memo } from 'react'
import styled from 'styled-components'

import {
  useGetListingAdventuresByProximityQuery,
  GetListingAdventuresByProximityQueryResult,
} from 'hooks/useQueries'
import { useMediaQueryContext } from 'contexts/MediaQueryContext'

import { media, mediaMax } from '../../../styles/media'

import Button from '../../../components/display/Button'
import TrackVisibility from '../../../components/utils/TrackVisibility'
import {
  HorizontalListWrapper,
  HorizontalListTrack,
  HorizontalList,
  HorizontalListItem,
} from '../../../components/display/HorizontalList'

import AdventureCard, { AdventureCardLoading } from './AdventureCard'

export const Outer = styled.div``

const Inner = styled.div`
  display: flex;
  overflow: hidden;

  ${media.md`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: var(--ds-space-05);
  `}
`

const ButtonWrapper = styled.div`
  grid-column: 1 / -1;
  width: 100%;

  ${Button} {
    margin: 0 auto;
  }
`

const ItemWrapper = styled.div`
  box-sizing: border-box;

  ${mediaMax.md`
    height: 100%;
    min-width: 264px;
    &:not(:last-child) {
      margin-bottom: 24px;
    }
  `}
`

const MobileCardList = styled(HorizontalList)`
  display: flex;
  position: relative;
`

const MobileLoading = memo(() => (
  <>
    {[...Array(6)].map((_, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <HorizontalListItem key={i}>
        <ItemWrapper>
          <AdventureCardLoading />
        </ItemWrapper>
      </HorizontalListItem>
    ))}
  </>
))

const Loading = memo(() => (
  <>
    {[...Array(6)].map((_, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <ItemWrapper key={i}>
        <AdventureCardLoading />
      </ItemWrapper>
    ))}
  </>
))

type AdventureCardListDisplayProps = {
  adventureEdges: GetListingAdventuresByProximityQueryResult['data']['listing']['adventuresByProximity']['edges']
  loading?: boolean
  hasNextPage?: boolean
  onFetchMore?: (e?: MouseEvent<HTMLButtonElement>) => any
}

export const AdventureCardListDisplay = ({
  adventureEdges,
  loading,
  hasNextPage,
  onFetchMore,
}: AdventureCardListDisplayProps) => {
  const { md: largerThanMd } = useMediaQueryContext()
  const smallerThanMd = !largerThanMd

  if (smallerThanMd) {
    return (
      <HorizontalListWrapper style={{ marginLeft: '-24px' }}>
        <HorizontalListTrack>
          <MobileCardList>
            {adventureEdges?.map(({ node: adventure }) => (
              <HorizontalListItem key={adventure.id}>
                <ItemWrapper>
                  <AdventureCard {...adventure} />
                </ItemWrapper>
              </HorizontalListItem>
            ))}

            {loading && <MobileLoading />}

            {hasNextPage && !loading && (
              <TrackVisibility
                onChange={(isVisible) => {
                  if (isVisible) onFetchMore()
                }}
              >
                <MobileLoading />
              </TrackVisibility>
            )}
          </MobileCardList>
        </HorizontalListTrack>
      </HorizontalListWrapper>
    )
  }

  return (
    <Outer>
      <Inner>
        {adventureEdges?.map(({ node: adventure }) => (
          <ItemWrapper key={adventure.id}>
            <AdventureCard {...adventure} />
          </ItemWrapper>
        ))}

        {hasNextPage && (
          <ButtonWrapper>
            <Button onClick={onFetchMore} ghost>
              Show More
            </Button>
          </ButtonWrapper>
        )}

        {loading && <Loading />}
      </Inner>
    </Outer>
  )
}

type AdventureCardListProps = {
  listingId: string
}

export default ({ listingId }: AdventureCardListProps) => {
  const { data, loading, fetchMore } = useGetListingAdventuresByProximityQuery({
    variables: { listingId },
  })

  const adventureEdges = data?.listing?.adventuresByProximity?.edges

  const pageInfo = data?.listing?.adventuresByProximity?.pageInfo

  const handleFetchMore = (e?: MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault()

    fetchMore({
      variables: {
        afterCursor: pageInfo?.endCursor,
      },
    })
  }

  return (
    <AdventureCardListDisplay
      adventureEdges={adventureEdges}
      hasNextPage={pageInfo?.hasNextPage}
      loading={loading}
      onFetchMore={handleFetchMore}
    />
  )
}
